import { Apartment, Business, Close, Email, Phone, Search } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import get from "lodash/get";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import ProtectedPermissionView from "../../components/ProtectedPermissionView";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import surveyService from "../../services/survey";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

interface StatusOption {
  type: string,
  desc: string
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "400px",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
};

export default function SurveyList(props: any) {
  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({
    keyword: "",
  });
  const { user } = useAppSelector((state) => state.global);
  const [openModal, setOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [selectedSchedule, setSelectedSchedule] = useState(dayjs(new Date()));
  const [statusOption, setStatusOption] = useState<StatusOption[]>([])
  const notification = useNotification();
  const listSurvey = useQuery(
    ["survey-list", options],
    ({ queryKey }) => {
      return surveyService.getAllSurvey({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const updateArea = useMutation("update-survey", surveyService.updateSurvey, {
    onSuccess: () => {
      notification.onOpen({
        message: "Survey was successfully updated!",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      notification.onOpen({
        message:
          error?.response?.data?.message === undefined
            ? "Something went wrong"
            : error?.response?.data?.message,
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    updateArea.mutate({
      ...values,
      schedule: formUpdate.getValues("schedule").format("YYYY-MM-DD HH:mm:ss"),
      user_id: user.userId,
      username: user.username,
      id: selectedId,
    });
    setOpenModal(false);
    listSurvey.refetch();
  };

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const formUpdate = useForm({
    defaultValues: {
      schedule: selectedSchedule,
      username: "",
      user_id: "",
      status: "",
    },
  });

  const handleOpenModal = (value: any) => {
    setSelectedId(value.id);
    setSelectedSchedule(dayjs(value.schedule));
    formUpdate.setValue("status", ""); // Reset status when modal opens

    // Set available status options based on current status
    if (value.status.toLowerCase() === "scheduled") {
      setStatusOption([
        {
          type: "Confirmed",
          desc: "Confirmed"
        },
        {
          type: "Rejected",
          desc: "Rejected"
        }
      ]);
    } else if (value.status.toLowerCase() === "confirmed") {
      setStatusOption([
        {
          type: "Booked",
          desc: "Booked"
        },
        {
          type: "No-Booked",
          desc: "No Booked"
        }
      ]);
    } else {
      setStatusOption([]); // No options for rejected status
    }
    setOpenModal(true);
  };

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);
  return (
    <MainLayout>
      <Helmet>
        <title>Survey List</title>
      </Helmet>

      <Breadcrumb
        label={`Survey List`}
        breadcrumbs={[
          { label: "Survey Management", href: "/app/order/survey" },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="survey"
        requiredPermissions={{ is_read: true }}
      >
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
          </Stack>
        </Box>

        <Modal
          open={openModal}
          onClose={() => setOpenModal(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Stack direction="column" spacing={2}>
              <Typography variant="h1" mx={2}>
                Process Survey
              </Typography>
              <FormControl margin="normal">
                <FormLabel>Confirmed Schedule</FormLabel>
                <Controller
                  name="schedule"
                  control={formUpdate.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        ampm={false}
                        value={formUpdate.getValues("schedule")}
                        onChange={(newTime) =>
                          formUpdate.setValue(
                            "schedule",
                            newTime || dayjs(new Date())
                          )
                        }
                        closeOnSelect={false}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>
              <FormControl margin="normal">
                <FormLabel>Status</FormLabel>
                <Controller
                  name="status"
                  control={formUpdate.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Select
                      id="status"
                      value={formUpdate.getValues().status}
                      onChange={(event) => {
                        formUpdate.setValue("status", event.target.value);
                        setOptions((prev: any) => ({
                          ...prev,
                          month: Number(event.target.value),
                        }));
                      }}
                    >
                      <MenuItem value="">Select Status</MenuItem>
                      {statusOption.map(
                        (item: StatusOption) => (
                          <MenuItem value={item?.type} key={item?.type}>
                            {item?.desc}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  )}
                />
              </FormControl>
            </Stack>
            <LoadingButton
              sx={{ marginTop: "5px" }}
              variant="contained"
              onClick={formUpdate.handleSubmit(onSubmit)}
              loading={updateArea.isLoading}
            >
              SUBMIT
            </LoadingButton>
          </Box>
        </Modal>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listSurvey.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listSurvey, "data.data", [])}
            rowCount={get(listSurvey, "data.data", []).length}
            page={options.page_number}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "tenant_name",
                headerName: "Tenant Name",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography variant="inherit">{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "phone",
                headerName: "Contact",
                flex: 2,
                renderCell: (params) => {
                  return (
                    <Stack spacing={1} sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Phone sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="body2">
                          {params.row.phone}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Email sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="body2">
                          {params.row.email}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                },
              },
              {
                field: "unit_name",
                headerName: "Unit Details",
                flex: 2,
                renderCell: (params) => {
                  return (
                    <Stack spacing={1} sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Apartment sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="body2">
                          {params.row.room_name}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Business sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary">
                          {params.row.property_name}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                },
              },
              {
                field: "schedule",
                headerName: "Schedule",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography variant="inherit">
                        {dayjs(params.value).format("YYYY-MM-DD HH:mm:ss")}
                      </Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "status",
                headerName: "Status",
                flex: 1,
                renderCell: (params) => {
                  const getStatusStyle = (status: string) => {
                    const statusStyles: { [key: string]: { backgroundColor: string; color: string; border: string } } = {
                      rejected: {
                        backgroundColor: '#FFE5E5',
                        color: '#D32F2F',
                        border: '1px solid #FFA4A4'
                      },
                      cancelled: {
                        backgroundColor: '#FFF4E5',
                        color: '#ED6C02',
                        border: '1px solid #FFB74D'
                      },
                      confirmed: {
                        backgroundColor: '#E8F5E9',
                        color: '#2E7D32',
                        border: '1px solid #A5D6A7'
                      },
                      booked: {
                        backgroundColor: '#E3F2FD',
                        color: '#1976D2',
                        border: '1px solid #90CAF9'
                      },
                      'no-booked': {
                        backgroundColor: '#FFEBEE',
                        color: '#C62828',
                        border: '1px solid #EF9A9A'
                      },
                      scheduled: {
                        backgroundColor: '#F3E5F5',
                        color: '#7B1FA2',
                        border: '1px solid #CE93D8'
                      }
                    };
                    return statusStyles[status.toLowerCase()] || {
                      backgroundColor: '#F5F5F5',
                      color: '#616161',
                      border: '1px solid #E0E0E0'
                    };
                  };

                  return (
                    <Stack>
                      <Typography
                        variant="inherit"
                        sx={{
                          borderRadius: 2,
                          padding: '6px 12px',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textTransform: 'capitalize',
                          ...getStatusStyle(params.value)
                        }}
                      >
                        {params.value}
                      </Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "processed_by",
                headerName: "Processed By",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography variant="inherit">{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <ProtectedPermissionView
                        menuSlug="survey"
                        requiredPermissions={{ is_update: true }}
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          // Disable button if status is rejected, booked, or no-booked
                          disabled={["rejected", "booked", "no-booked"].includes(params.row.status.toLowerCase())}
                          onClick={() => handleOpenModal(params.row)}
                        >
                          Process
                        </Button>
                      </ProtectedPermissionView>
                    </Stack>
                  );
                },
              }
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
